import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { AppLayout, AuthLayout } from './layouts';
import { useAuth } from './hooks';
import {
  Auth,
  CommandCenter,
  Agents,
  AgentStudio,
  Sessions,
  SessionReplay,
  ChatPlayground,
  Analytics,
  DesignSystem,
} from './screens';

/**
 * Route guard — redirects unauthenticated users to /auth.
 */
const RequireAuth: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated } = useAuth();

  if (!isAuthenticated) {
    return <Navigate to="/auth" replace />;
  }

  return <>{children}</>;
};

/**
 * Route guard — redirects authenticated users away from auth pages.
 */
const RedirectIfAuth: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated } = useAuth();

  if (isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
};

const App: React.FC = () => (
  <Routes>
    {/* Auth routes — redirect to home if already logged in */}
    <Route
      element={
        <RedirectIfAuth>
          <AuthLayout />
        </RedirectIfAuth>
      }
    >
      <Route path="/auth" element={<Auth />} />
    </Route>

    {/* Protected app routes — redirect to /auth if not logged in */}
    <Route
      element={
        <RequireAuth>
          <AppLayout />
        </RequireAuth>
      }
    >
      <Route path="/" element={<CommandCenter />} />
      <Route path="/agents" element={<Agents />} />
      <Route path="/agents/:id" element={<AgentStudio />} />
      <Route path="/sessions" element={<Sessions />} />
      <Route path="/sessions/:id" element={<SessionReplay />} />
      <Route path="/playground" element={<ChatPlayground />} />
      <Route path="/analytics" element={<Analytics />} />
      <Route path="/design-system" element={<DesignSystem />} />
    </Route>

    {/* Catch-all redirect */}
    <Route path="*" element={<Navigate to="/" replace />} />
  </Routes>
);

export default App;
